const PINATA_IPFS_GATEWAY_MARKER = 'mypinata.cloud/ipfs';
const PINATA_THUMBNAIL_PARAMS = 'img-quality=30&img-format=webp';
const PINATA_IMAGE_PARAM_PREFIX = 'img-';

/**
 * Standard render widths. Values are 2x the CSS box so the image stays sharp on
 * retina displays without shipping the full-resolution original.
 *
 * TABLE — .rw-market-table__thumb is a hard 44x44 (styles.css:6000).
 * CARD  — card grids are width:100% with a fixed height (styles.css 581/768/2498).
 */
export const PINATA_WIDTH_TABLE = 88;
export const PINATA_WIDTH_CARD = 320;

/**
 * Appends Pinata image-optimization params to a gateway URL.
 *
 * Without `img-width` the gateway returns the full-resolution original and the browser
 * downscales it, so a 44px table thumbnail could cost megabytes on a throttled
 * connection. Passing a width is the single largest byte saving available here.
 *
 * @param url   Any media URL. Non-Pinata URLs are returned untouched.
 * @param width Optional render width in px; omitted means "no resize" (previous behaviour).
 */
export function getPinataThumbnailUrl(url: string | null | undefined, width?: number): string | null | undefined {
    if (!url || typeof url !== 'string') return url;

    if (!url.includes(PINATA_IPFS_GATEWAY_MARKER)) return url;

    // Asking the optimizer again for a file it has already refused costs its 2-3s rejection
    // every time, so a known case goes straight to the original.
    if (isPinataUnoptimizable(url)) return stripPinataImageParams(url);

    const hasQuality = url.includes('img-quality=') || url.includes('img-format=');
    const hasWidth = url.includes('img-width=');

    // Already fully optimised for this call — nothing to add.
    if (hasQuality && (hasWidth || !width)) return url;

    const params: string[] = [];
    if (!hasQuality) params.push(PINATA_THUMBNAIL_PARAMS);
    if (width && !hasWidth) params.push(`img-width=${Math.round(width)}`);

    if (!params.length) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${params.join('&')}`;
}

/**
 * Removes the image-optimization params `getPinataThumbnailUrl` adds, leaving the
 * original object URL.
 *
 * The gateway optimizer cannot decode every format it is handed: an AVIF source
 * answers any `img-*` request with 404 ("The request timed out searching for a file
 * on the non-pinata IPFS network"), even though the plain URL serves the file at 200.
 * JPEG, PNG, WebP and GIF all optimize fine, so the failure is per-format and cannot
 * be predicted from the URL -- a gateway URL is a bare CID with no extension.
 *
 * The image fallback directive therefore retries with this stripped URL before giving
 * up on an image, which costs one request for an unsupported format and nothing at
 * all for the formats that work.
 *
 * Non-Pinata URLs, and URLs carrying no `img-*` params, are returned untouched.
 */
export function stripPinataImageParams(url: string | null | undefined): string | null | undefined {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes(PINATA_IPFS_GATEWAY_MARKER)) return url;

    const queryStart = url.indexOf('?');
    if (queryStart === -1) return url;

    const base = url.slice(0, queryStart);
    const rest = url.slice(queryStart + 1);
    const hashStart = rest.indexOf('#');
    const query = hashStart === -1 ? rest : rest.slice(0, hashStart);
    const hash = hashStart === -1 ? '' : rest.slice(hashStart);

    // Only the optimizer's own params go; anything else on the URL is left alone.
    const kept = query
        .split('&')
        .filter((param) => param && !param.toLowerCase().startsWith(PINATA_IMAGE_PARAM_PREFIX));

    if (kept.length === query.split('&').filter(Boolean).length) return url;

    return `${base}${kept.length ? `?${kept.join('&')}` : ''}${hash}`;
}

/**
 * Originals this browser session has seen the gateway refuse to optimize but serve as-is.
 *
 * The optimizer rejects some formats outright (AVIF answers 415 after 2-3s), and that cannot
 * be told from a bare CID. Once an optimized URL has failed and its original has loaded, the
 * answer is known, so later requests for the same file skip the optimizer instead of paying
 * for the rejection again - which is what made switching back to such an image look stuck.
 *
 * Held in memory only: a reload starts afresh, so a one-off gateway hiccup is never remembered
 * beyond the session.
 */
const unoptimizableOriginals = new Set<string>();

/**
 * Records that the gateway serves this file but cannot optimize it.
 *
 * @param url The original URL that loaded after its optimized form failed. Any `img-*` params
 *            are ignored, so the optimized form may be passed as well.
 */
export function markPinataUnoptimizable(url: string | null | undefined): void {
    const original = stripPinataImageParams(url);
    if (typeof original === 'string' && original.includes(PINATA_IPFS_GATEWAY_MARKER)) {
        unoptimizableOriginals.add(original);
    }
}

/**
 * Whether the gateway is already known to refuse optimizing this file.
 *
 * @param url Original or optimized URL of the file.
 */
export function isPinataUnoptimizable(url: string | null | undefined): boolean {
    const original = stripPinataImageParams(url);
    return typeof original === 'string' && unoptimizableOriginals.has(original);
}
